import React, { useCallback, useEffect, useState } from 'react';
import {
  KompisSheetBody,
  KompisSheetBodyPadding,
  KompisSheetFooter,
  KompisSheetFooterButtons
} from '@inter-ikea-kompis/react-components';
import styled from 'styled-components';
import media from 'ui/media';
import Body from 'ui/Typography/Body';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import Title from 'ui/Typography/Title';
import SlideOutMenu from 'components/SlideOutMenu/SlideOutMenu';
import Box from 'ui/Box';
import { useIntl } from 'react-intl';
import { useCore } from 'contexts/core';
import { ProductType } from '@inter-ikea-storageone/range';
import {
  deleteUncoupledSlidingDoors,
  getUncoupledSlidingDoors,
  isSlidingDoorUncoupled,
  PlatsaUncoupledSlidingDoorComponent
} from '@inter-ikea-storageone/range-platsa';
import { useAppDispatch } from 'ducks/hooks';
import { setSavePossible } from 'ducks/layout';
import { SkapaTheme } from '@inter-ikea-kompis/themes';

const TitleWrapper = styled.div`
  ${media.mobile`
    width: 100%;
    padding: 0.5rem 0;
  `}
`;

/**
 * Menu is displayed in case you drag a PLATSA sliding door so that it
 * no longer is aligned with another sliding door (i.e. invlid placed)
 */
const UncoupledSlidingDoorsMenu = () => {
  const [visible, setVisible] = useState(false);
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const dispatch = useAppDispatch();

  const onHistoryEvent = useCallback(() => {
    const slidingDoorsInScene = coreApi.entities.getByProductType(
      ProductType.PLATSA_SLIDING_DOOR_ASM
    );
    const uncoupledSlidingDoorsInScene = getUncoupledSlidingDoors(coreApi).length > 0;
    setVisible(uncoupledSlidingDoorsInScene);
    dispatch(setSavePossible(!uncoupledSlidingDoorsInScene));
    slidingDoorsInScene.forEach(door => {
      if (isSlidingDoorUncoupled(door, coreApi)) {
        door.addComponent(new PlatsaUncoupledSlidingDoorComponent());
      } else if (door.hasComponent(PlatsaUncoupledSlidingDoorComponent.id)) {
        door.removeComponent(PlatsaUncoupledSlidingDoorComponent.id);
      }
    });
  }, [coreApi, dispatch]);

  useEffect(() => {
    const unsubscribers = [
      coreApi.subscribe('history', onHistoryEvent),
      coreApi.subscribe('reset', onHistoryEvent),
      coreApi.subscribe('load', onHistoryEvent)
    ];
    return () => {
      unsubscribers.forEach(unsubscribe => {
        unsubscribe();
      });
    };
  }, [coreApi, onHistoryEvent]);

  const onRemove = () => {
    coreApi.history.historyAction(() => {
      deleteUncoupledSlidingDoors(coreApi);
    });
  };

  return (
    <SlideOutMenu trigger={!!visible}>
      <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <KompisSheetBody theme={SkapaTheme}>
          <KompisSheetBodyPadding theme={SkapaTheme}>
            <TitleWrapper>
              <Title pb="s" pt="l" type={ThemeFontStyleTypeEnum.headingMedium}>
                {formatMessage({ id: 'storageone.uncoupledSlidingDoorsSheetTitle' })}
              </Title>
            </TitleWrapper>
            <Body py="md" mb="xxxl" type={ThemeFontStyleTypeEnum.bodyMedium}>
              {formatMessage({ id: 'storageone.uncoupledSlidingDoorsSheetDescription' })}
            </Body>
          </KompisSheetBodyPadding>
        </KompisSheetBody>
        <KompisSheetFooter theme={SkapaTheme}>
          <KompisSheetFooterButtons
            compact={false}
            data-testid="uncoupled-sliding-doors-remove-btn"
            theme={SkapaTheme}
            primaryLabel={formatMessage({
              id: 'storageone.uncoupledSlidingDoorsSheetRemoveButton'
            })}
            onButtonClick={onRemove}
          />
        </KompisSheetFooter>
      </Box>
    </SlideOutMenu>
  );
};

export default UncoupledSlidingDoorsMenu;
